import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowLeft, ArrowRight, BookOpen, Check, Clock, Award, Lock, Trophy, FileText,
} from "lucide-react";

export default function TrackDetail() {
  const { trackId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/tracks/${trackId}`);
        setData(d);
      } catch {
        // Track detail fetch failed; the !data render branch handles UX
      } finally {
        setLoading(false);
      }
    })();
  }, [trackId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="max-w-5xl mx-auto px-6 py-20 text-zinc-500 font-mono text-sm text-center">Loading...</main>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="max-w-5xl mx-auto px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold tracking-tight mb-2">Track not found</h1>
          <Link to="/tracks" className="text-red-600 hover:underline">← Back to tracks</Link>
        </main>
      </div>
    );
  }

  // Compute total + done lessons
  const totalLessons = data.modules.reduce((a, m) => a + m.lessons.length, 0);
  const doneLessons = data.modules.reduce((a, m) => a + m.lessons.filter((l) => l.completed).length, 0);
  const progress = totalLessons ? Math.round((doneLessons / totalLessons) * 100) : 0;
  const allLessonsDone = doneLessons === totalLessons && totalLessons > 0;
  const fe = data.final_exam;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="track-detail-page">
        <Link to="/tracks" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="track-back">
          <ArrowLeft className="w-4 h-4" /> All tracks
        </Link>

        {/* Hero */}
        <div className="card-tech p-8 mb-8" style={{ borderTop: `6px solid ${data.color}` }}>
          <div className="overline mb-2">{data.short_name}</div>
          <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter mb-3">
            {data.name}
          </h1>
          <p className="text-zinc-700 leading-relaxed mb-5 max-w-3xl">{data.description}</p>
          <div className="flex items-center gap-5 text-sm text-zinc-700 font-mono flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {data.hours} hours</span>
            <span className="inline-flex items-center gap-1.5"><BookOpen className="w-4 h-4" /> {data.modules.length} modules</span>
            <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4" /> {data.credential}</span>
          </div>

          {/* Progress */}
          <div className="mt-6">
            <div className="flex justify-between text-xs font-mono text-zinc-600 mb-1.5">
              <span>{doneLessons}/{totalLessons} lessons complete</span>
              <span>{progress}%</span>
            </div>
            <div className="bg-zinc-100 h-2">
              <div className="h-full transition-all" style={{ width: `${progress}%`, background: data.color }} />
            </div>
          </div>
        </div>

        {/* Modules */}
        <div className="space-y-6 mb-8">
          {data.modules.map((m, idx) => (
            <div key={m.id} className="card-tech p-6" data-testid={`track-module-${m.id}`}>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                  <div className="overline mb-1">{m.title.split('—')[0].trim()}</div>
                  <h2 className="font-heading text-xl font-bold tracking-tight">{m.title}</h2>
                  <div className="text-xs font-mono text-zinc-500 mt-1">{m.hours} hours · {m.lessons.length} lessons</div>
                </div>
              </div>

              {/* Lessons */}
              <div className="space-y-1.5 mb-4">
                {m.lessons.map((les) => (
                  <Link
                    key={les.id}
                    to={`/lesson/${les.id}`}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 border border-zinc-100 hover:border-red-600 hover:bg-red-50/30 transition-colors"
                    data-testid={`track-lesson-${les.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-mono font-bold flex-shrink-0 ${les.completed ? "bg-green-600 text-white" : "bg-zinc-200 text-zinc-600"}`}>
                        {les.completed ? <Check className="w-3.5 h-3.5" /> : <BookOpen className="w-3 h-3" />}
                      </div>
                      <span className="text-sm text-zinc-800 truncate">{les.title}</span>
                    </div>
                    <span className="text-[11px] font-mono text-zinc-500 whitespace-nowrap">{les.hours}h · open →</span>
                  </Link>
                ))}
              </div>

              {/* Module quiz */}
              {m.quizzes.length > 0 && (
                <div className="border-t border-zinc-100 pt-3 mt-3">
                  {m.quizzes.map((q) => (
                    <Link
                      key={q.id}
                      to={`/quiz/${q.id}`}
                      className="flex items-center justify-between gap-3 px-3 py-2.5 bg-zinc-50/50 hover:bg-red-50/50 transition-colors"
                      data-testid={`track-quiz-${q.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-mono font-bold flex-shrink-0 ${q.passed ? "bg-green-600 text-white" : "bg-amber-500 text-white"}`}>
                          <FileText className="w-3 h-3" />
                        </div>
                        <span className="text-sm font-semibold text-zinc-800 truncate">{q.title}</span>
                        {q.best_score_pct !== null && (
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 ${q.passed ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-900"}`}>
                            {q.best_score_pct.toFixed(0)}%
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] font-mono text-zinc-500 whitespace-nowrap">{q.questions} q · ≥{q.passing_score_pct}% to pass</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Final exam + cert */}
        {fe && (
          <div className="card-tech p-6 border-2 border-red-200 mb-6" data-testid="track-final-exam">
            <div className="overline mb-2"><span className="red-bar" />Track Final Exam</div>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">{fe.title}</h2>
                <p className="text-sm text-zinc-700 leading-relaxed">
                  {fe.total_questions}-question proctored-style final. {fe.time_limit_minutes}-minute time limit. Pass with {fe.passing_score_pct}%+ to earn the
                  <strong> {data.credential}</strong> credential.
                </p>
              </div>
              {fe.passed ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 text-xs font-mono uppercase tracking-widest font-bold">
                  <Trophy className="w-3.5 h-3.5" /> Passed · {fe.best_score_pct?.toFixed(0)}%
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-900 text-xs font-mono uppercase tracking-widest font-bold">
                  <Lock className="w-3.5 h-3.5" /> Not yet passed
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link to={`/quiz/${fe.id}`} className="btn-primary" data-testid="track-final-take-btn">
                {fe.passed ? "Re-take final exam" : "Start the final exam"} <ArrowRight className="w-4 h-4" />
              </Link>
              {fe.passed && allLessonsDone && (
                <span className="inline-flex items-center gap-1.5 px-4 py-3 bg-green-50 text-green-800 text-sm font-semibold border border-green-300">
                  <Award className="w-4 h-4" /> Track complete!
                </span>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
